import {useQuery} from '@tanstack/react-query'
import i18next from 'i18next'
import {Button} from 'primereact/button'
import {Divider} from 'primereact/divider'
import {Dropdown} from 'primereact/dropdown'
import {InputTextarea} from 'primereact/inputtextarea'
import {MultiSelect} from 'primereact/multiselect'
import {Sidebar} from 'primereact/sidebar'
import {Skeleton} from 'primereact/skeleton'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useParams} from 'react-router-dom/cjs/react-router-dom.min'
import {getGenericInfo, getPageGenericInfo} from '../api/ServiceWorker'
import {ReactComponent as BsRobot} from '../assets/icons/BsRobot.svg'
import {useAuth} from '../components/authentication/AuthProvider'
import {component} from '../components/form/Constant'
import {showNotification} from '../components/notification/NotificationService'
import TranslateUtils from '../i18n/TranslateUtils'
import {aiFormKeys, aiOptionsValue} from './AIConstant'
import {convertObsDataToAIValue, getAiDataList, includeObsKeys} from './AiUtils'
import './FloatingWidget.css'
import {useAIContext} from './OpenAiProvider'
import StyledAiButton from './StyledAiButton'
export const aiOptionsType = {
    longList: 'longList',
    stringList: 'stringList',
    arrayList: 'arrayList',
    obsList: 'obsList',
}
const abbreviationKeys = {
    heartRate: 'HR',
    respiration: 'RR',
    temperature: 'T',
    oxygenSaturation: 'SpO2',
    bloodPressure: 'BP',
    bloodGlucoseLevel: 'BLG',
}

const PARAMETER_PATH = 'parameter'
const parameterQueryKey = 'Parameter'
const AiWidget = ({
    value = '',
    obsData = [],
    formData = {},
    isObsForm = false,
    onApply = () => {},
}) => {
    const widgetRef = useRef(false)
    const [aiData, setAiData] = useState({
        [aiFormKeys.compareWith]: includeObsKeys,
    })
    const {
        aiResponse,
        openAiWidget,
        onHideWidget,
        toggleOpenAi,
        skeleton,
        score,
        response,
        aiOptions,
        setAiOptions,
        onSendAiRequest,
        handleOnDetector,
        handleOnParaphrase,
    } = useAIContext()
    const {id: idString} = useParams()
    const {translateCommons, translateOptions} = TranslateUtils()
    const patientId = useMemo(() => {
        return !!idString ? parseInt(idString) : null
    }, [idString])
    const {user} = useAuth()

    const responseOutputRef = useRef(null)

    const {data: parameterData} = useQuery(
        [parameterQueryKey, user?.id, patientId],
        () => getGenericInfo(PARAMETER_PATH, user?.id, patientId),
        {
            enabled: !!patientId && !!user?.id && !!isObsForm,
        },
    )
    const isEmptyData = useMemo(() => {
        let isEmpty = true
        if (!!isObsForm && formData) {
            for (let i = 0; i < includeObsKeys.length; i++) {
                if (!!formData[includeObsKeys[i]]) {
                    return false
                }
            }
        }
        return isEmpty
    }, [formData, isObsForm])

    //region
    const {data: currentObservationData} = useQuery(
        [
            'Health-Record',
            user?.id,
            0,
            aiData?.record,
            'createdAt',
            -1,
            {userId: patientId},
        ],
        () =>
            getPageGenericInfo(
                'healthRecord',
                user?.id,
                0,
                aiData?.record,
                'createdAt',
                -1,
                {userId: patientId},
            ),
        {
            enabled:
                !!patientId &&
                !!user?.id &&
                !!isObsForm &&
                !!aiData?.record &&
                aiData?.record > 0,
        },
    )

    const composeData = useMemo(() => {
        let data = []
        if (formData && !isEmptyData) {
            data = [formData]
        }
        if (!!isObsForm) {
            if (
                currentObservationData?.content &&
                Array.isArray(currentObservationData?.content) &&
                currentObservationData.content?.length > 0
            ) {
                data = [...currentObservationData.content, ...data]
            }
        }

        return data
    }, [formData, isEmptyData, isObsForm, currentObservationData])
    //endRegion
    const handleOnChange = (value, isOption = false) => {
        if (!value) {
            return
        }
        if (!!isOption) {
            setAiOptions(value)
            onSendAiRequest(value)
        } else {
            onSendAiRequest(value)
        }
    }

    // Lấy ngôn ngữ đang sử dụng từ i18next
    const currentLanguage = i18next.language
    const handleOnSubmit = () => {
        if (!!isObsForm) {
            if (!aiData[aiFormKeys.observation]) {
                showNotification('warn', 'Please select an observation option')
                return
            }

            let stringValue = ''

            // Compare with others
            if (aiData[aiFormKeys.observation] !== aiOptionsValue.onlyCurrent) {
                if (
                    !aiData[aiFormKeys.compareWith] ||
                    aiData[aiFormKeys.compareWith] < 1
                ) {
                    showNotification(
                        'warn',
                        'Please select health criterias to compare with',
                    )

                    return
                }
                if (
                    !aiData[aiFormKeys.record] ||
                    aiData[aiFormKeys.record] < 1
                ) {
                    showNotification(
                        'warn',
                        'Please select a number of records to compare with',
                    )

                    return
                }

                if (
                    !currentObservationData?.content ||
                    currentObservationData?.content.length < 1
                ) {
                    showNotification('warn', 'No observation data to process')
                    return
                }

                stringValue = convertObsDataToAIValue(
                    composeData,
                    parameterData,
                    aiData,
                )
            }

            // Only current
            if (aiData[aiFormKeys.observation] === aiOptionsValue.onlyCurrent) {
                if (
                    !formData ||
                    Object.keys(formData).length < 1 ||
                    isEmptyData
                ) {
                    showNotification('warn', 'No observation data to process')
                    return
                }

                let parameterDataMapped = ''
                let parameterPromptString = ''
                Object.keys(parameterData).forEach(key => {
                    parameterPromptString += ` ${key}: ${parameterData[key]}`
                })

                let obsDataMapped = ''
                Object.keys(formData).forEach(key => {
                    if (formData[key]) {
                        obsDataMapped += `${key}: ${formData[key]}, `
                    }
                })

                stringValue = `Can you provide a summary of health observations based on the following parameters: ${parameterDataMapped} and the following observations: ${obsDataMapped}`
            }

            if (!stringValue) {
                return ''
            }

            const prompt = `${stringValue} ${
                aiData?.question || 'Overall Analysis and Summary'
            } & 'Replace "diagnosis" by "summary", Translate into ${currentLanguage}`

            onSendAiRequest(prompt)
        } else {
            if (aiData?.question) {
                onSendAiRequest(aiData?.question)
            }
        }
    }
    const dataList = useMemo(() => {
        return getAiDataList(aiData, isObsForm)
    }, [aiData, isObsForm])

    const renderAiForm = useCallback(() => {
        if (!dataList || !Array.isArray(dataList) || dataList.length < 1) {
            return
        }
        return dataList.map(
            (item, idx) =>
                !!item.isShow && (
                    <div key={idx} className={`${item.className ?? ' col-12'}`}>
                        <h5 className="font-semibold my-1">
                            {translateCommons(item.label)}
                        </h5>
                        {item.component === component.dropdown ? (
                            <Dropdown
                                className="col-12 "
                                placeholder={translateCommons('Select an item')}
                                value={aiData[item.key]}
                                options={translateOptions(item.options)}
                                onChange={e =>
                                    setAiData({...aiData, [item.key]: e.value})
                                }
                            />
                        ) : item.component === component.multiSelect ? (
                            <MultiSelect
                                value={aiData[item.key]}
                                options={translateOptions(item.options)}
                                onChange={e =>
                                    setAiData({...aiData, [item.key]: e.value})
                                }
                                optionLabel="label"
                                display="chip"
                                placeholder={translateCommons(
                                    'Select criterias',
                                )}
                                className="col-12"
                            />
                        ) : (
                            item.component === component.inputTextArea && (
                                <InputTextarea
                                    className="col-12"
                                    value={aiData[item.key]}
                                    placeholder={translateCommons('Input here')}
                                    autoResize
                                    rows={4}
                                    onChange={e =>
                                        setAiData({
                                            ...aiData,
                                            [item.key]: e.target.value,
                                        })
                                    }
                                />
                            )
                        )}
                    </div>
                ),
        )
    }, [aiData, dataList])

    const handleOnApply = () => {
        onApply(aiResponse)
        onHideWidget()
    }

    useEffect(() => {
        if (responseOutputRef.current) {
            responseOutputRef.current.style.height = 'auto'
            responseOutputRef.current.style.height =
                responseOutputRef.current.scrollHeight + 'px'
        }
    }, [aiResponse])

    return (
        <div className="widget-container w-full " ref={widgetRef}>
            <div className="flex justify-content-end">
                <StyledAiButton onClick={toggleOpenAi} />
            </div>
            <Sidebar
                className=""
                style={{width: '50%'}}
                visible={openAiWidget}
                position="right"
                onHide={onHideWidget}>
                {' '}
                <div className="widget-box w-full">
                    <div className="flex align-items-center">
                        <BsRobot
                            className="m-1 mr-2"
                            style={{width: '24px', height: '24px'}}
                        />{' '}
                        <h4 className="my-2 text-700">
                            {translateCommons('Report Assistant')}
                        </h4>
                    </div>
                    <div className="grid">{renderAiForm()}</div>
                    <div className="col-12">
                        <Button
                            icon="pi pi-upload"
                            className="col-12 h-3rem p-button-secondary"
                            label={translateCommons('SUBMIT REQUEST')}
                            onClick={handleOnSubmit}
                        />
                    </div>

                    <Divider className="my-2" />
                    <div className="col-12  text-800 text-sm pt-2  my-2  p-2">
                        <h5 className="font-semibold my-1">
                            {translateCommons('Response')}
                        </h5>
                        {!!skeleton ? (
                            <div className="bg-gray-50">
                                <Skeleton className="col-12 mb-1"></Skeleton>
                                <Skeleton
                                    width="12rem"
                                    className="col-6 mb-1"></Skeleton>
                                <Skeleton
                                    width="6rem"
                                    className="col-4 mb-1"></Skeleton>
                            </div>
                        ) : (
                            <InputTextarea
                                ref={responseOutputRef}
                                className="w-full"
                                value={aiResponse}
                                placeholder="Response goes here..."
                                autoResize
                                readOnly={!aiResponse}
                                rows={5}
                                onChange={() => {
                                    return
                                }}
                            />
                        )}
                    </div>
                    <div className="grid col-12 mb-5">
                        <Button
                            className=" mr-1 col-12 h-3rem"
                            icon="pi pi-check-circle"
                            label={translateCommons('APPLY RESPONSE')}
                            onClick={handleOnApply}
                        />
                        {/* <Button
                            className={`${
                                response
                                    ? 'bg-gray-700 hover:bg-gray-600 border-gray-600'
                                    : 'bg-gray-600 border-gray-600'
                            }    mr-1`}
                            label={translateCommons('Paraphrase')}
                            onClick={handleOnParaphrase}
                        />{' '} */}
                    </div>
                </div>
            </Sidebar>
        </div>
    )
}

export default AiWidget
